import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Send, Lock, ListChecks, QrCode, UserPlus, CalendarDays, Pencil, X, FileText, Star } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import CollectTab from "@/components/admin/CollectTab";
import AddTicketTab from "@/components/admin/AddTicketTab";
import DatesTab from "@/components/admin/DatesTab";
import ContentTab from "@/components/admin/ContentTab";
import ReviewsTab from "@/components/admin/ReviewsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "horseshoe_admin_token";

const money = (p) => `£${(p / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function LoginView({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[100svh] flex items-center justify-center px-6 relative" data-testid="admin-login">
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative z-10 w-full max-w-sm ornate-frame bg-[var(--surface)] p-10">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={ASSETS.crest} alt="" className="w-14 mb-4 rounded-full" />
          <p className="uppercase tracking-[0.35em] text-[11px] text-[var(--gold)] flex items-center gap-2"><Lock className="w-3 h-3" /> Staff Only</p>
          <h1 className="font-display text-4xl text-[var(--cream)] mt-3">Admin</h1>
        </div>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Username</label>
            <input data-testid="admin-username-input" value={username} onChange={(e) => setUsername(e.target.value)} className="field-input" required />
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Password</label>
            <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="field-input" required />
          </div>
          <button data-testid="admin-login-btn" type="submit" disabled={loading}
            className="w-full rounded-full bg-[var(--gold)] text-[var(--bg)] py-4 uppercase tracking-[0.2em] text-xs font-semibold flex items-center justify-center gap-2 hover:bg-[var(--cream)] transition-colors duration-300 disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div className="ornate-frame bg-[var(--surface)] p-6">
      <p className="uppercase tracking-[0.2em] text-[10px] text-[var(--gold)]">{label}</p>
      <p className="font-display text-4xl text-[var(--cream)] mt-2">{value}</p>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [dates, setDates] = useState([]);
  const [selectedDateId, setSelectedDateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingsRes, datesRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, auth),
        axios.get(`${API}/admin/dates`, auth),
      ]);
      setData(bookingsRes.data);
      setDates(datesRes.data.dates || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Could not load bookings");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const resend = async (ref) => {
    setResending(ref);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${ref}/resend`, {}, auth);
      toast.success(`Confirmation resent to ${data.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend");
    } finally {
      setResending(null);
    }
  };

  const openEdit = (b) => {
    setEditing(b);
    setEditName(b.name || "");
    setEditEmail(b.email || "");
  };

  const saveEdit = async () => {
    if (editEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/admin/bookings/${editing.booking_ref}`,
        { name: editName, email: editEmail }, auth);
      toast.success("Booking updated.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update booking.");
    } finally {
      setSavingEdit(false);
    }
  };

  const allBookings = data?.bookings || [];
  const selectedDate = dates.find((d) => d.id === selectedDateId);
  const bookings = allBookings.filter((b) => b.date_id === selectedDateId);
  const paidForDate = bookings.filter((b) => b.payment_status === "paid");
  const dateStats = selectedDate ? {
    tickets_sold: selectedDate.sold,
    capacity: selectedDate.capacity,
    remaining: selectedDate.remaining,
    checked_in: paidForDate.reduce((s, b) => s + (b.checked_in_count || 0), 0),
    paid_bookings: paidForDate.length,
    revenue_pence: paidForDate.reduce((s, b) => s + (b.source !== "manual" ? (b.amount || 0) : 0), 0),
  } : null;

  const TABS = [
    { id: "bookings", label: "Bookings", icon: ListChecks },
    { id: "collect", label: "Collect", icon: QrCode },
    { id: "add", label: "Add ticket", icon: UserPlus },
    { id: "dates", label: "Dates", icon: CalendarDays },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "content", label: "Content", icon: FileText },
  ];

  return (
    <main className="min-h-[100svh] px-4 sm:px-8 lg:px-12 py-10" data-testid="admin-dashboard">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <img src={ASSETS.crest} alt="" className="w-12 rounded-full" />
          <div>
            <p className="uppercase tracking-[0.3em] text-[10px] text-[var(--gold)]">The Horseshoe · Admin</p>
            <h1 className="font-display text-2xl sm:text-3xl text-[var(--cream)]">Beer Explained</h1>
          </div>
        </div>
        <button data-testid="admin-logout-btn" onClick={onLogout} className="flex items-center gap-2 border border-[rgba(201,162,74,0.4)] text-[var(--cream)] px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200 self-start">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </header>

      <nav className="flex flex-wrap gap-2 mb-10 border-b border-[rgba(201,162,74,0.2)]" data-testid="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors duration-200 ${active ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-[var(--muted)] hover:text-[var(--cream)]"}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </nav>

      {tab === "bookings" && (
        <>
          <div className="mb-8">
            <p className="uppercase tracking-[0.25em] text-[11px] text-[var(--gold)] mb-4">Choose a date to view its bookings</p>
            <div className="flex flex-wrap gap-3" data-testid="admin-date-cards">
              {loading && dates.length === 0 && <Loader2 className="w-5 h-5 animate-spin text-[var(--gold)]" />}
              {dates.map((d) => {
                const active = d.id === selectedDateId;
                return (
                  <button
                    key={d.id}
                    data-testid={`admin-date-card-${d.id}`}
                    onClick={() => setSelectedDateId(active ? null : d.id)}
                    className={`px-5 py-4 border text-left transition-colors duration-200 ${active ? "border-[var(--gold)] bg-[rgba(201,162,74,0.12)]" : "border-[rgba(201,162,74,0.3)] hover:border-[var(--gold)]"}`}
                  >
                    <span className="font-serif-alt text-lg text-[var(--cream)] block">{d.label}</span>
                    <span className="font-body text-[10px] uppercase tracking-[0.15em] text-[var(--muted)]">
                      {d.is_past ? "Past event" : d.sales_closed ? "Sales closed" : "On sale"}
                    </span>
                  </button>
                );
              })}
              {!loading && dates.length === 0 && (
                <p className="font-serif-alt text-lg text-[var(--muted)]">No dates yet — add one in the Dates tab.</p>
              )}
            </div>
          </div>

          {!selectedDate && !loading && (
            <div className="ornate-frame bg-[var(--surface)] py-16 text-center" data-testid="admin-select-date-prompt">
              <p className="font-serif-alt text-xl text-[var(--muted)]">Select a date above to see its totals and bookings.</p>
            </div>
          )}

          {selectedDate && (
            <>
              {dateStats && (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-10" data-testid="admin-date-stats">
                  <Stat label="Tickets sold" value={`${dateStats.tickets_sold} / ${dateStats.capacity}`} />
                  <Stat label="Places left" value={dateStats.remaining} />
                  <Stat label="Checked in" value={dateStats.checked_in} />
                  <Stat label="Bookings" value={dateStats.paid_bookings} />
                  <Stat label="Revenue" value={money(dateStats.revenue_pence)} />
                </div>
              )}

              <div className="flex justify-end mb-4">
                <button data-testid="admin-refresh-btn" onClick={load} className="flex items-center gap-2 border border-[rgba(201,162,74,0.4)] text-[var(--cream)] px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200">
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>

              <div className="ornate-frame bg-[var(--surface)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-bookings-table">
                <thead>
                  <tr className="uppercase tracking-[0.15em] text-[10px] text-[var(--gold)] border-b border-[rgba(201,162,74,0.25)]">
                    <th className="py-4 px-5">Reference</th>
                    <th className="py-4 px-5">Name</th>
                    <th className="py-4 px-5">Date</th>
                    <th className="py-4 px-5">Email</th>
                    <th className="py-4 px-5">Tickets</th>
                    <th className="py-4 px-5">In</th>
                    <th className="py-4 px-5">Paid</th>
                    <th className="py-4 px-5">Source</th>
                    <th className="py-4 px-5">Status</th>
                    <th className="py-4 px-5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="font-body text-sm text-[var(--cream)]">
                  {loading && (
                    <tr><td colSpan={10} className="py-16 text-center text-[var(--muted)]"><Loader2 className="w-6 h-6 animate-spin inline" /></td></tr>
                  )}
                  {!loading && bookings.length === 0 && (
                    <tr><td colSpan={10} className="py-16 text-center text-[var(--muted)] font-serif-alt text-lg" data-testid="admin-no-bookings">No bookings for this date yet.</td></tr>
                  )}
                  {!loading && bookings.map((b) => {
                    const paid = b.payment_status === "paid";
                    const fully = (b.checked_in_count ?? 0) >= b.quantity && b.quantity > 0;
                    return (
                      <tr key={b.booking_ref} className="border-b border-[rgba(201,162,74,0.12)] hover:bg-[rgba(201,162,74,0.05)] transition-colors duration-200">
                        <td className="py-4 px-5 font-semibold text-[var(--gold)] tracking-wide">{b.booking_ref}</td>
                        <td className="py-4 px-5">{b.name || "—"}</td>
                        <td className="py-4 px-5 text-[var(--muted)] whitespace-nowrap">{b.date_label || "—"}</td>
                        <td className="py-4 px-5 break-all">{b.email}</td>
                        <td className="py-4 px-5">{b.quantity}</td>
                        <td className="py-4 px-5">
                          <span className={fully ? "text-[var(--gold)]" : "text-[var(--muted)]"}>{b.checked_in_count ?? 0}/{b.quantity}</span>
                        </td>
                        <td className="py-4 px-5">{money(b.amount)}</td>
                        <td className="py-4 px-5 text-[var(--muted)] capitalize">{b.source || "online"}</td>
                        <td className="py-4 px-5">
                          <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-[0.15em] border ${paid ? "border-[var(--gold)] text-[var(--gold)]" : "border-[rgba(157,179,162,0.5)] text-[var(--muted)]"}`}>
                            {paid ? "Paid" : b.payment_status}
                          </span>
                        </td>
                        <td className="py-4 px-5 text-right whitespace-nowrap">
                          <button
                            data-testid={`admin-edit-${b.booking_ref}`}
                            onClick={() => openEdit(b)}
                            className="inline-flex items-center gap-2 border border-[rgba(201,162,74,0.4)] text-[var(--cream)] px-3 py-2 text-[11px] uppercase tracking-[0.15em] hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200 mr-2">
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            data-testid={`admin-resend-${b.booking_ref}`}
                            disabled={!paid || resending === b.booking_ref}
                            onClick={() => resend(b.booking_ref)}
                            className="inline-flex items-center gap-2 rounded-full bg-[var(--gold)] text-[var(--bg)] px-4 py-2 text-[11px] uppercase tracking-[0.15em] font-semibold hover:bg-[var(--cream)] transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed">
                            {resending === b.booking_ref ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Resend
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
            </>
          )}
        </>
      )}

      {tab === "collect" && <CollectTab token={token} />}
      {tab === "add" && <AddTicketTab token={token} onDone={load} />}
      {tab === "dates" && <DatesTab token={token} onChange={load} />}
      {tab === "reviews" && <ReviewsTab token={token} />}
      {tab === "content" && <ContentTab token={token} />}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="edit-booking-modal" onClick={() => setEditing(null)}>
          <div className="ornate-frame bg-[var(--surface)] p-8 w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setEditing(null)} data-testid="edit-close-btn" className="absolute top-4 right-4 text-[var(--muted)] hover:text-[var(--cream)] transition-colors duration-200">
              <X className="w-5 h-5" />
            </button>
            <p className="uppercase tracking-[0.3em] text-[10px] text-[var(--gold)]">Edit booking</p>
            <h3 className="font-display text-3xl text-[var(--cream)] mt-2 mb-6">{editing.booking_ref}</h3>
            <div className="space-y-5">
              <div>
                <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Name</label>
                <input data-testid="edit-name-input" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Guest name" className="field-input" />
              </div>
              <div>
                <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Email</label>
                <input data-testid="edit-email-input" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className="field-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <button data-testid="edit-cancel-btn" onClick={() => setEditing(null)} className="flex-1 border border-[rgba(201,162,74,0.4)] text-[var(--cream)] py-3 text-xs uppercase tracking-[0.15em] hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200">
                  Cancel
                </button>
                <button data-testid="edit-save-btn" onClick={saveEdit} disabled={savingEdit} className="flex-1 rounded-full bg-[var(--gold)] text-[var(--bg)] py-3 text-xs uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:bg-[var(--cream)] transition-colors duration-200 disabled:opacity-50">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  useEffect(() => {
    document.title = "Admin · The Horseshoe";
  }, []);

  if (!token) return <LoginView onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
